import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  OnInit,
  runInInjectionContext,
  signal,
  Signal,
  viewChild,
  viewChildren,
  WritableSignal,
} from '@angular/core';

import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';

import { Router } from '@angular/router';
import { TopNotificationsService } from '@core/services/top-notifications.service';
import { NotificationsService } from '@core/components/notifications/services/notifications.service';
import { SpinnerService } from '@shared/services/spinner/spinner.service';
import { TranslatePipe } from '@ngx-translate/core';
import { DropdownCloseDirective } from '@shared/directives/dropdown-close.directive';

import { ButtonComponent, BadgeComponent } from '@ctti-dev/ng-gencat';
import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { CallOutComponent } from '@shared/components/callout/callout.component';

import { Callout } from '@shared/models/callout.model';
import { TopNotificationModel } from '@shared/models/notifications.model';

import { InViewportDirective } from '@shared/directives/in-viewport.directive';
import { ButtonConfigDirective } from '@shared/directives/button-config.directive';
import { TERTIARY_BUTTON } from '@shared/utils/utils.config';
import { UserService } from '@core/services/user.service';

@Component({
  selector: 'app-header-notifications',
  standalone: true,
  imports: [
    CallOutComponent,
    BadgeComponent,
    NgbDropdownModule,
    DropdownCloseDirective,
    TranslatePipe,
    DropdownComponent,
    ButtonComponent,
    InViewportDirective,
    ButtonConfigDirective,
  ],
  templateUrl: './header-notifications.component.html',
  styleUrl: './header-notifications.component.scss',
})
export class HeaderNotificationsComponent implements OnInit {
  readonly tertiaryButton = TERTIARY_BUTTON;

  limitCharsTruncateText: number = 80;

  renderCalloutsSignal: WritableSignal<boolean> = signal<boolean>(false);

  notificationsDropdownSignal: Signal<DropdownComponent | undefined> =
    viewChild<DropdownComponent>('appDropdown');

  calloutsSignal: Signal<ReadonlyArray<ElementRef>> = viewChildren(
    CallOutComponent,
    { read: ElementRef },
  );

  private lastElapsedTimeHeaderNotificationsFlag = new Map<string, string>();

  private readonly _topNotificationsService = inject(TopNotificationsService);
  private readonly _notificationsService = inject(NotificationsService);
  private readonly _router = inject(Router);
  private readonly _contextInjector = inject(Injector);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _userService = inject(UserService);

  readonly topNotificationsBuiltCalloutsSignal: Signal<Partial<Callout>[]> =
    computed(() =>
      this._topNotificationsService.topNotificationsBuiltCalloutsSignal(),
    );

  readonly areUnreadTopNotificationsSignal: Signal<boolean> = computed(() =>
    this._topNotificationsService.areUnreadTopNotificationsSignal(),
  );

  readonly headerTopNotificationsSignal: Signal<TopNotificationModel> =
    computed(() =>
      this._topNotificationsService.selectedTopNotificationSignal(),
    );

  readonly elapsedHeaderNotificationsTimeTitlesSignal: Signal<string[]> =
    computed(() => {
      const alreadyRenderedLabels = new Set<string>();

      return this._topNotificationsService
        .topNotificationsSignal()

        .map((notification) => {
          const elapsedTimeTitle: string =
            this.createTopNotificationsElapsedDate(
              notification.generationInstant,
            );

          if (alreadyRenderedLabels.has(elapsedTimeTitle)) return '';
          alreadyRenderedLabels.add(elapsedTimeTitle);
          return elapsedTimeTitle;
        });
    });

  ngOnInit(): void {
    this._topNotificationsService.triggerLoadTopNotifications();
    this._reactToTopNotificationsContent();
  }

  onDropdownOpened(): void {
    if (!this._topNotificationsService.isTopNotificationsAgencyRoleValid()) {
      this.closeNotificationsDropdown();
      return;
    }
    this._spinnerService.spinFor(700, {
      afterSpin: [
        () => {
          this.renderCalloutsSignal.set(true);
        },
      ],
    });
  }

  acessSelectedTopNotificationArea(index: number) {
    const topNotifications: TopNotificationModel[] =
      this._topNotificationsService.topNotificationsSignal();

    this._topNotificationsService.setSelectedTopNotification(
      topNotifications[index],
    );

    if (!this.headerTopNotificationsSignal().read) {
      this._topNotificationsService.markSelectedTopNotificationAsRead();
    }

    this.closeNotificationsDropdown();
    this._topNotificationsService.redirectToProperDestination(
      this.headerTopNotificationsSignal().redirectExpedition,
      this.headerTopNotificationsSignal().redirectJourney,
      this.headerTopNotificationsSignal().redirectLogicalZones,
      this.headerTopNotificationsSignal().redirectRatesComercialLines,
      this.headerTopNotificationsSignal().redirectRatesOrigenDesti,
    );
  }

  goToNotificationsPage(): void {
    this._router.navigateByUrl('/home/notifications').then(() => {
      this._notificationsService.triggerGetNotificationsReload();
    });
  }

  private closeNotificationsDropdown(): void {
    const dropdown: DropdownComponent | undefined =
      this.notificationsDropdownSignal();
    dropdown?.close();
  }

  private _reactToTopNotificationsContent(): void {
    runInInjectionContext(this._contextInjector, () => {
      effect(() => {
        this._topNotificationsService.topNotificationsSignal();
        this.lastElapsedTimeHeaderNotificationsFlag.clear();
      });
    });
  }

  private createTopNotificationsElapsedDate(datetime: string): string {
    if (this.lastElapsedTimeHeaderNotificationsFlag.has(datetime)) {
      return '';
    }

    const elapsedTimeTitle: string =
      this._topNotificationsService.mapNotificationsElapsedDateTitle(datetime);

    this.lastElapsedTimeHeaderNotificationsFlag.set(datetime, elapsedTimeTitle);
    return elapsedTimeTitle;
  }

  onNotificationsHeaderCalloutView(index: number): void {
    const onViewCallout: TopNotificationModel =
      this._topNotificationsService.topNotificationsSignal()[index];

    if (
      this._topNotificationsService.isAgencyRoleAllowed() &&
      !this._topNotificationsService.isUserRoleAllowed(
        this._userService.userRoles(),
      ) &&
      !onViewCallout.read
    ) {
      this._notificationsService.markViewedNotificationAsRead(onViewCallout.id);
    }
  }
}
